// ── Drag pointer-move ──
// Originally extracted from useFurnitureInteraction's onPointerDown: the body of the
// nested `onPointerMove` handler. Closure variables arrive through one context object.
//
// ── Freshness of reassignable closure variables ──
// `dragSurfaceMap` / `dragFootprintMap` / `dragSurfaceItemFootprintMap` are reassigned
// during the lifetime of a drag (in onPointerDown and again in onPointerUp's `finally`),
// so they are passed as GETTERS, invoked once per (fully synchronous) invocation.
//
// `rawEffectiveWidthTiles` / `rawEffectiveHeightTiles` are assigned exactly once during
// setupFurnitureInteraction — before any handler can run — so they are passed by value.
import * as PIXI from 'pixi.js';
import type { QueryClient } from '@tanstack/react-query';
import { stageLayout } from '../state/layoutSync';
import { isRugCategory, getTileDims } from '../../../domain/tileMath';
import { asFurnitureMarker, asFurnitureSprite, liveFurnitureData } from '../render/pixiExtensions';
import {
  checkSurfaceOverlap,
  isFullyOnSurface,
  footprintHasConflict,
  getFurnitureDepthComponents,
  getFurnitureDepth,
  getOccupiedBaseTile,
  type SurfaceTileMap,
} from '../../../domain/depthMath';
import { footprintOutOfRoom } from '../../../domain/footprintTolerance';
import { validateWindowPlacement } from '../../../domain/windowPlacement';
import { applyWallArtFacing } from '../render/wallArtFacing';
import { drawWallGuides, snapWallItemToGuides, wallFarEndOvershoot, type WallGuideDragState } from './wallGuideDrag';
import type { CarriedItem } from './surfaceCarry';
import { drawDragFootprintOverlay } from '../render/overlays';
import { computeTouchLift, touchLiftEase } from '../../../domain/touchLift';
import { impactOccurred, selectionChanged } from '../../../utils/telegram/hapticFeedback';
import { playSfx } from '../../../utils/sfx';
import { redrawOccupiedTilesOverlay } from '../render/occupiedOverlay';
import type { RenderFurniture } from '../../../domain/types';
import type {
  RoomSceneFurniture,
  RoomSceneDrag,
  RoomSceneOverlays,
  RoomSceneFlags,
} from '../state/roomSceneState';
import {
  clampWallFloorOffset,
  wallItemHeightPx,
  wallVerticalSpan,
  WINDOW_VERTICAL_OFFSET,
  type WindowSnapResult,
} from '../../../domain/windowSnap';

// Moved here with the handler — used ONLY by the drag pointer-move logic.
const DRAG_THRESHOLD = 8;

// Z-index boost used during DRAG only — large so the dragged item is clearly
// visible above the surface it might be placed on. NOTE: this is deliberately
// different from depthMath's SURFACE_PLACEMENT_DELTA (0.5), which is the settled
// ordering applied by recomputeAllZIndexes once the drag ends. Anything left
// holding this transient value after a drop is a bug.
export const SURFACE_ZINDEX_BOOST = 1500;

// Z-index for floor items in footprint conflict. Must be: max_depth < CONFLICT_FLOOR_ZINDEX < SURFACE_ZINDEX_BOOST.
// Max depth ≈ room max pixel-Y ≈ 500; 1000 is safely between floor and surface layers.
// Exported: useFurnitureInteraction's onPointerUp still applies it after a drop.
export const CONFLICT_FLOOR_ZINDEX = 1000;

/**
 * Structural shape of the drag state this handler needs. Declared here rather than
 * imported from useFurnitureInteraction so this module has NO back-import into its
 * source file (same approach as room/dropCommit.ts).
 */
export interface DragMoveDragState {
  isDragging: boolean;
  marker: PIXI.Container;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
  previousWindowSnap?: WindowSnapResult;
  pointerType?: string;
  liftPx?: number;
  /** Picked up by holding the finger still (dragSession) — a drag without movement yet. */
  pickedUp?: boolean;
  /** The finger has actually travelled past DRAG_THRESHOLD (a real move, not just a hold). */
  moved?: boolean;
  /** When the lift started rising (performance.now()). */
  liftStartedAt?: number;
  /** 0 → 1 rise of the lift; dragSession keeps re-running the move until it is 1. */
  liftProgress?: number;
  /** Last red/green state — a change gives a haptic tick. */
  lastInvalid?: boolean;
  /** Wall items: height on the wall chosen by the drag (wall_floor_offset), saved on drop. */
  wallOffset?: number;
  wallGuideBoxes?: WallGuideDragState['wallGuideBoxes'];
  wallGuideKey?: string;
}

export interface DragMoveContext {
  scene: DragMoveScene;
  refs: DragMoveRefs;
  item: DragMoveItem;
  geometry: DragMoveGeometry;
  snap: DragMoveSnapHelpers;
  dragMaps: DragMoveMaps;
  carried: DragMoveCarriedItems;
  services: DragMoveServices;
  removeGreenOutline: () => void;
}

export interface DragMoveScene {
  getRoomContainer: () => PIXI.Container | null;
}

export interface DragMoveRefs {
  furniture: RoomSceneFurniture;
  drag: RoomSceneDrag;
  overlays: RoomSceneOverlays;
  flags: RoomSceneFlags;
}

export interface DragMoveItem {
  sprite: PIXI.Sprite;
  furniture: RenderFurniture;
}

export interface DragMoveGeometry {
  tileDimW: number | null | undefined;
  tileDimH: number | null | undefined;
  interactionIsRug: boolean;
  isWindow: boolean;
  rawEffectiveWidthTiles: number;
  rawEffectiveHeightTiles: number;
  /** Live catalog read at call time so admin tile edits are picked up (see original). */
  getLiveCatalog: () => RenderFurniture['furniture_catalog'] | undefined;
  liveWallFloorOffset: number;
}

export interface DragMoveSnapHelpers {
  snapWindowAnchorToGrid: (
    anchorX: number,
    anchorY: number,
    cursorWorldX?: number,
    previousSnap?: WindowSnapResult,
  ) => WindowSnapResult;
}

export interface DragMoveMaps {
  /** Reassigned across the drag → getters, invoked once per frame. */
  getDragSurfaceMap: () => SurfaceTileMap;
  getDragFootprintMap: () => Map<string, number>;
  getDragSurfaceItemFootprintMap: () => Map<string, number>;
}

export interface DragMoveCarriedItems {
  carriedItems: CarriedItem[];
  carriedSet: Set<number>;
}

export interface DragMoveServices {
  mapObjectId: number;
  queryClient: QueryClient;
}

export function handleDragPointerMove(
  moveEvent: PIXI.FederatedPointerEvent,
  ctx: DragMoveContext,
): void {
  const {
    scene: {
      getRoomContainer,
    },
    refs: {
      furniture: furnitureScene,
      drag,
      overlays,
      flags,
    },
    item: {
      sprite,
      furniture,
    },
    geometry: {
      tileDimW,
      tileDimH,
      interactionIsRug,
      isWindow,
      rawEffectiveWidthTiles,
      rawEffectiveHeightTiles,
      getLiveCatalog,
      liveWallFloorOffset,
    },
    snap: {
      snapWindowAnchorToGrid,
    },
    carried: {
      carriedItems,
      carriedSet,
    },
    services: {
      mapObjectId,
      queryClient,
    },
    removeGreenOutline,
  } = ctx;

  // Reassignable outer state — read once per invocation (see header note).
  const dragSurfaceMap = ctx.dragMaps.getDragSurfaceMap();
  const dragFootprintMap = ctx.dragMaps.getDragFootprintMap();
  const dragSurfaceItemFootprintMap = ctx.dragMaps.getDragSurfaceItemFootprintMap();

        const roomContainer = getRoomContainer(); // live module state (see onPointerDown)
        if (!roomContainer) return;
        const dragState = drag.state;
        if (!dragState || !dragState.marker) return;
        
        // Use marker from dragState instead of closure to avoid stale references
        const currentMarker = dragState.marker;
        
        // Check if marker still exists and is valid
        if (!currentMarker || !currentMarker.position) {
          return;
        }

        const dist = Math.sqrt(
          Math.pow(moveEvent.global.x - dragState.startX, 2) +
          Math.pow(moveEvent.global.y - dragState.startY, 2)
        );

        if (dist > DRAG_THRESHOLD) dragState.moved = true;
        if (dist > DRAG_THRESHOLD || dragState.pickedUp) {
          const wasDragging = dragState.isDragging;
          dragState.isDragging = true;
          const currentSprite = currentMarker.children?.[0] as PIXI.Sprite | undefined;
          if (currentSprite) {
            currentSprite.cursor = 'grabbing';
          }
          if (!wasDragging) {
            // Remove selection outline while dragging (re-applied on drop)
            removeGreenOutline();
            // Lift above the finger: fixed for the whole gesture, in screen px, from
            // where the finger first held the item relative to the item's bottom edge
            // (measured after the outline is gone so it does not count as the item).
            dragState.liftPx = computeTouchLift(dragState.pointerType, dragState.startY, currentMarker.getBounds().bottom);
            // The lift RISES over time (see below) — no jump; dragSession re-runs this
            // handler every frame until it has risen, even if the finger is still.
            dragState.liftStartedAt = performance.now();
            dragState.liftProgress = dragState.liftPx ? 0 : 1;
            impactOccurred('light'); // picked up
            playSfx('lift');

            // For rugs: on drag start, immediately elevate z-order above all other rugs.
            // This ensures the dragged rug visually appears on top during the entire drag.
            if (interactionIsRug) {
              let maxOrder = 0;
              for (const [mId, m] of furnitureScene.markers.entries()) {
                if (mId === furniture.id) continue;
                const mData = liveFurnitureData(
                  asFurnitureSprite(m.children[0] as PIXI.Sprite | undefined),
                  asFurnitureMarker(m),
                );
                if (mData && isRugCategory(mData.furniture_catalog?.category)) {
                  maxOrder = Math.max(maxOrder, mData.rug_z_order ?? 0);
                }
              }
              const newOrder = maxOrder + 1;
              // Update PIXI z-index immediately for visual feedback
              const liveFData = liveFurnitureData(
                asFurnitureSprite(currentSprite),
                asFurnitureMarker(currentMarker),
              );
              if (liveFData) {
                const { w: rW, h: rH } = getTileDims(liveFData.furniture_catalog);
                const baseDepth = getFurnitureDepth(currentMarker, rW, rH, true);
                currentMarker.zIndex = baseDepth + newOrder * 0.001;
              }
              // Sync rug_z_order on the sprite's furnitureData (inline, syncFurnitureData is in a different scope)
              {
                const _sp = asFurnitureSprite(currentMarker.children[0] as PIXI.Sprite | undefined);
                const _mk = asFurnitureMarker(currentMarker);
                if (_sp?.furnitureData) {
                  _sp.furnitureData = { ..._sp.furnitureData, rug_z_order: newOrder };
                }
                if (_mk?.furnitureData) {
                  _mk.furnitureData = { ..._mk.furnitureData, rug_z_order: newOrder };
                }
              }
              // Persist so the priority is saved even without movement (local first,
              // backend in the next layout batch).
              stageLayout(queryClient, mapObjectId, [{ id: furniture.id, rug_z_order: newOrder }]);
            }
          }

          const localPos = roomContainer.toLocal(moveEvent.global);
          const newX = localPos.x - dragState.offsetX;
          // Touch: the item rides above the finger (screen px → room units here, so
          // the lift looks the same at every zoom). Everything below — landing zone,
          // conflicts, surfaces — works from where the item is SHOWN.
          let liftLocal = 0;
          if (dragState.liftPx) {
            dragState.liftProgress = touchLiftEase(performance.now() - (dragState.liftStartedAt ?? 0));
            const liftedPoint = roomContainer.toLocal({ x: moveEvent.global.x, y: moveEvent.global.y - dragState.liftPx * dragState.liftProgress });
            liftLocal = localPos.y - liftedPoint.y;
          }
          const newY = localPos.y - dragState.offsetY - liftLocal;
          const curX = currentMarker.position.x;
          const curY = currentMarker.position.y;

          let finalX: number;
          let finalY: number;

          if (isWindow) {
            // ── Window-specific drag: auto-mirror + snap to nearest wall ──
            // SIMPLE APPROACH: Windows work just like other objects but use snapWindowAnchorToGrid
            // which forces them onto walls. This is the ONLY snap logic needed.
            // snapWindowAnchorToGrid already handles: wall detection, tile snapping, mirror state
            //
            // CRITICAL: Subtract WINDOW_VERTICAL_OFFSET from newY before calling snap!
            // The marker position has WINDOW_VERTICAL_OFFSET baked in (added during placement).
            // Since offset = cursor - marker, newY = cursor - (cursor - marker.y) ≈ marker.y + delta,
            // which INCLUDES the WINDOW_VERTICAL_OFFSET. But snapWindowAnchorToGrid expects
            // floor-level coordinates and adds WINDOW_VERTICAL_OFFSET to the OUTPUT.
            // Without this subtraction, we'd get double offset → wrong tile → window stuck.
            // Pass raw cursor worldX so wall selection uses the cursor position (not the
            // biased anchor position). This fixes corner transitions: when the user drags
            // from the left wall towards the right wall, the cursor crosses the room diagonal
            // much sooner than the anchor does (because anchor = cursor - offset, and offset
            // can be large when the window started far on a wall).
            //
            // Also compute the PREVIOUS snap from the current marker position. This is used
            // to preserve distance from the corner when transitioning between walls:
            // e.g., baseTileY=4 on left wall → baseTileX=4 on right wall.
            // Subtract both WINDOW_VERTICAL_OFFSET and wall_floor_offset so snap sees floor-level coords.
            // Use dragState.previousWindowSnap (set on previous move frame) as the hysteresis reference
            // instead of recomputing from the marker position. Recomputing without cursorWorldX gives
            // wrong wall detection near the corner (rawTileX > rawTileY is unreliable there), which
            // caused the item to jump or teleport prematurely when using the hysteresis.
            const snapAt = (dx: number) => snapWindowAnchorToGrid(newX + dx, newY - WINDOW_VERTICAL_OFFSET - liveWallFloorOffset, localPos.x + dx, dragState.previousWindowSnap);
            let ws = snapAt(0);
            
            // SIMPLE: snapWindowAnchorToGrid already handles ALL constraints:
            // - Forces window onto wall (baseTileX=0 or baseTileY=0)
            // - Prevents corner (baseTileY>=1 on left wall, baseTileX>=1 on right wall)
            // - Clamps to valid range [0, maxBaseTile]
            // No extra canWindowMoveFurther needed!
            
            // Along the wall from the snap (it stays ON the wall), height from the
            // finger: the item moves freely in both wall axes, kept inside the wall.
            // ws.y is floor-level + WINDOW_VERTICAL_OFFSET; the rest is its height
            // on the wall (wall_floor_offset), saved on drop.
            const itemHeight = currentSprite ? wallItemHeightPx(currentSprite.width, currentSprite.height) : 0;
            const clampOffset = (offset: number) => clampWallFloorOffset(offset, itemHeight, rawEffectiveHeightTiles);
            let wallOffset = clampOffset(newY - ws.y);
            // Pressed against the wall's top/bottom or its far end: drop the overshoot
            // so turning back moves the item at once instead of after a dead zone
            // (the touch lift alone pushes an item at the top ~100 px past it). The
            // corner end keeps it — pushing past the corner is how it changes walls.
            dragState.offsetY += newY - ws.y - wallOffset;
            if (wallFarEndOvershoot(ws, newX, dragState.previousWindowSnap)) dragState.offsetX += newX - ws.x;

            if (currentSprite) {
              // Auto-mirror to face nearest wall
              const currentlyMirrored = currentSprite.scale.x < 0;
              if (ws.shouldMirror !== currentlyMirrored) {
                const absScaleX = Math.abs(currentSprite.scale.x);
                currentSprite.scale.x = ws.shouldMirror ? -absScaleX : absScaleX;
                const fs = asFurnitureSprite(currentSprite)!;
                applyWallArtFacing(fs);
                const baseVisualOffsetX = fs.__visualBaseOffsetX ?? 0;
                currentSprite.x = currentSprite.scale.x < 0 ? -baseVisualOffsetX : baseVisualOffsetX;
                fs.furnitureData = {
                  ...(fs.furnitureData ?? furniture),
                  is_mirrored: ws.shouldMirror,
                };
              }

              // Smart guides (Figma-style): a light magnet onto other wall items'
              // edges and centres, with neon lines while aligned.
              const snapped = snapWallItemToGuides({
                state: dragState,
                room: roomContainer,
                markers: furnitureScene.markers,
                furnitureId: furniture.id,
                marker: currentMarker,
                sprite: currentSprite,
                ws,
                wallOffset,
                resnapX: snapAt,
                clampOffset,
              });
              ws = snapped.ws;
              wallOffset = snapped.wallOffset;
              drawWallGuides(overlays.wallGuides, dragState, snapped.guides, roomContainer);
            }

            // Save this snap result so the next frame has a correct previous snap for hysteresis.
            dragState.previousWindowSnap = ws;
            dragState.wallOffset = wallOffset;
            finalX = ws.x;
            finalY = ws.y + wallOffset;

            if (currentSprite) {
              // Visual tint: red/transparent when NOT flush with wall, normal when on wall
              if (ws.isOnWall) {
                // Use snap's baseTile directly (no getOccupiedBaseTile — it has 1-tile gap for windows)
                const overlayW = ws.shouldMirror ? rawEffectiveHeightTiles : rawEffectiveWidthTiles;
                const overlayH = ws.shouldMirror ? rawEffectiveWidthTiles : rawEffectiveHeightTiles;
                const { valid } = validateWindowPlacement(
                  furnitureScene.markers,
                  ws.baseTileX, ws.baseTileY,
                  overlayW, overlayH,
                  furniture.id,
                  wallVerticalSpan(wallOffset, rawEffectiveHeightTiles, itemHeight),
                );
                currentSprite.tint = valid ? 0xFFFFFF : 0xFF4444;
                currentSprite.alpha = valid ? 1 : 0.5;
                if (dragState.lastInvalid !== undefined && dragState.lastInvalid !== !valid) selectionChanged();
                dragState.lastInvalid = !valid;
              } else {
                currentSprite.tint = 0xFF4444;
                currentSprite.alpha = 0.5;
              }

            }
            // For windows, don't skip when only position unchanged — mirror/tint may differ
          } else {
            // Free movement: the item goes exactly where it is dragged and is saved
            // there — nothing blocks it, not even a wall. The grid is bookkeeping only
            // (footprints, conflicts, depth); a placement in a wall or off the floor
            // just shows red (footprintOutOfRoom below).
            finalX = newX;
            finalY = newY;
            if (finalX === curX && finalY === curY) return;
          }

          if (currentMarker.position) {
            currentMarker.position.set(finalX, finalY);

            // Move all carried items with the dragged item, preserving their relative offsets.
            for (const carried of carriedItems) {
              if (carried.marker.position) {
                carried.marker.position.set(finalX + carried.offsetX, finalY + carried.offsetY);
              }
            }

            // Z-index: boost above the surface owner if item overlaps any surface tile.
            // Rugs never land on surfaces — skip surface check for them.
            // dragSurfaceMap already excludes the dragged item, so no excludeFurnitureId needed.
            let isItemOnSurface = false;
            let isOversizedForSurface = false; // on a surface but doesn't fully fit
            if (dragSurfaceMap.size > 0 && !interactionIsRug) {
              const depthComp = getFurnitureDepthComponents(currentMarker, tileDimW, tileDimH, interactionIsRug);
              const isMirNow = sprite.scale.x < 0;
              const base = getOccupiedBaseTile(depthComp, isMirNow);
              const fpTiles = getLiveCatalog()?.footprint_tiles;
              const itemTileCount = fpTiles?.length ?? (depthComp.effectiveWidthTiles * depthComp.effectiveHeightTiles);
              const { isOnSurface, maxOwnerZIndex } = checkSurfaceOverlap(
                base.rawBaseTileX, base.rawBaseTileY,
                base.overlayWidthTiles, base.overlayHeightTiles,
                dragSurfaceMap,
                undefined,
                itemTileCount,
              );
              isItemOnSurface = isOnSurface;
              if (isOnSurface) {
                const centerTileX = Math.round(depthComp.tileX);
                const centerTileY = Math.round(depthComp.tileY);
                const fullyFits = isFullyOnSurface(
                  fpTiles,
                  centerTileX, centerTileY,
                  base.rawBaseTileX, base.rawBaseTileY,
                  base.overlayWidthTiles, base.overlayHeightTiles,
                  dragSurfaceMap, undefined, itemTileCount, isMirNow,
                );
                isOversizedForSurface = !fullyFits;
              }
              currentMarker.zIndex = isOnSurface
                ? maxOwnerZIndex + SURFACE_ZINDEX_BOOST
                : getFurnitureDepth(currentMarker, tileDimW, tileDimH, interactionIsRug);
            } else {
              currentMarker.zIndex = getFurnitureDepth(currentMarker, tileDimW, tileDimH, interactionIsRug);
            }

            // Explicitly keep carried items above the dragged item on every frame.
            // This ensures z-order is correct even if recomputeAllZIndexes temporarily
            // reset their z-indexes to raw depth between pointer events.
            for (const carried of carriedItems) {
              carried.marker.zIndex = currentMarker.zIndex + SURFACE_ZINDEX_BOOST;
            }

            // Tint: red when the spot is not valid — hanging off a surface, overlapping
            // another item, or (on the floor) in a wall / past the floor edge. Never
            // blocks the move; it only tells the player.
            if (!isWindow && currentSprite) {
              const depthNow = getFurnitureDepthComponents(currentMarker, tileDimW, tileDimH, interactionIsRug);
              const isMirNow = sprite.scale.x < 0;
              const fpTiles = interactionIsRug ? null : getLiveCatalog()?.footprint_tiles;
              let invalid: boolean;
              if (isOversizedForSurface) {
                invalid = true;
              } else if (isItemOnSurface) {
                invalid = dragSurfaceItemFootprintMap.size > 0
                  && footprintHasConflict(fpTiles, depthNow, isMirNow, dragSurfaceItemFootprintMap);
              } else {
                invalid = footprintOutOfRoom(fpTiles, depthNow, isMirNow)
                  || (!interactionIsRug && dragFootprintMap.size > 0
                    && footprintHasConflict(fpTiles, depthNow, isMirNow, dragFootprintMap));
              }
              currentSprite.tint = invalid ? 0xFF4444 : 0xFFFFFF;
              currentSprite.alpha = invalid ? 0.5 : 1;
              if (invalid && !isItemOnSurface) {
                currentMarker.zIndex = CONFLICT_FLOOR_ZINDEX;
              }
              // A tick whenever the spot turns red or green again — felt, not just seen.
              if (dragState.lastInvalid !== undefined && dragState.lastInvalid !== invalid) selectionChanged();
              dragState.lastInvalid = invalid;
            }

            // Make items that cover the dragged marker semi-transparent during drag
            // so the user can see what they're dragging
            const newTransparent = new Map<PIXI.Container, number>();
            const draggedBounds = currentMarker.getBounds();
            for (const [fId, otherMarker] of furnitureScene.markers.entries()) {
              if (fId === furniture.id) continue;
              if (carriedSet.has(fId)) continue; // carried items move with the drag — don't fade them
              if (otherMarker.zIndex <= currentMarker.zIndex) continue;
              const otherBounds = otherMarker.getBounds();
              const overlaps = !(
                draggedBounds.right < otherBounds.left ||
                draggedBounds.left > otherBounds.right ||
                draggedBounds.bottom < otherBounds.top ||
                draggedBounds.top > otherBounds.bottom
              );
              if (overlaps) {
                const storedAlpha = drag.transparentDuringDrag.has(otherMarker)
                  ? drag.transparentDuringDrag.get(otherMarker)!
                  : otherMarker.alpha;
                newTransparent.set(otherMarker, storedAlpha);
                otherMarker.alpha = 0.4;
              }
            }
            // Restore markers no longer covering the dragged item
            for (const [otherMarker, origAlpha] of drag.transparentDuringDrag.entries()) {
              if (!newTransparent.has(otherMarker)) {
                otherMarker.alpha = origAlpha;
              }
            }
            drag.transparentDuringDrag = newTransparent;

            // Drag footprint overlay: colour the item's footprint tiles green (valid) or red (conflict)
            if (!isWindow && !interactionIsRug && overlays.dragFp) {
              const fpOvr = overlays.dragFp;
              const depthFp = getFurnitureDepthComponents(currentMarker, tileDimW, tileDimH, false);
              const centerTX = Math.round(depthFp.tileX);
              const centerTY = Math.round(depthFp.tileY);
              const fpTilesOvr = getLiveCatalog()?.footprint_tiles;
              const isMirOvr = sprite.scale.x < 0;

              // Re-derive conflict state using the same maps as the tint logic above
              let ovrHasConflict = isOversizedForSurface;
              if (!ovrHasConflict && !isItemOnSurface) {
                ovrHasConflict = footprintOutOfRoom(fpTilesOvr, depthFp, isMirOvr)
                  || (dragFootprintMap.size > 0 && footprintHasConflict(fpTilesOvr, depthFp, isMirOvr, dragFootprintMap));
              }
              if (!ovrHasConflict && isItemOnSurface && dragSurfaceItemFootprintMap.size > 0) {
                ovrHasConflict = footprintHasConflict(fpTilesOvr, depthFp, isMirOvr, dragSurfaceItemFootprintMap);
              }

              // Drawing extracted to room/render/overlays.ts
              drawDragFootprintOverlay(fpOvr, ovrHasConflict, fpTilesOvr, isMirOvr, centerTX, centerTY, depthFp);
            } else if (overlays.dragFp) {
              overlays.dragFp.clear();
              overlays.dragFp.visible = false;
            }

            redrawOccupiedTilesOverlay(furnitureScene.markers, overlays.occupiedTiles, flags.showGridMode);
          }
        }
}
